import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Shield, Search, ChevronRight, AlertTriangle, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import apiClient from '../../services/apiClient';

const UserManagementTab = () => {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortBy, setSortBy] = useState('email');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async (token = null) => {
    try {
      if (token) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams();
      params.append('limit', '50');
      if (token) params.append('nextToken', token);

      const response = await apiClient.get(`/admin/users?${params.toString()}`);
      console.log('Users API response:', response.data.users);

      if (token) {
        setUsers(prev => [...prev, ...response.data.users]);
      } else {
        setUsers(response.data.users);
      }
      setNextToken(response.data.nextToken);
    } catch (err) {
      console.error('Error loading users:', err);
      if (err.response?.status === 403) {
        setError(t('admin.access_denied'));
      } else {
        setError(err.message || t('common.error'));
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (nextToken && !loadingMore) {
      loadUsers(nextToken);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'CONFIRMED': 'badge-success',
      'UNCONFIRMED': 'badge-warning',
      'FORCE_CHANGE_PASSWORD': 'badge-warning',
      'COMPROMISED': 'badge-error',
      'DISABLED': 'badge-error',
    };
    return styles[status] || 'badge-info';
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortedAndFilteredUsers = useMemo(() => {
    const filtered = users.filter(user =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
          break;
        case 'role':
          aVal = a.groups?.includes('admin') ? 1 : 0;
          bVal = b.groups?.includes('admin') ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, searchTerm, sortBy, sortOrder]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-text mb-1">
          {t('admin.users.title')}
        </h2>
        <p className="text-sm text-text-muted">
          {t('admin.users.description')}
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle size={18} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            &times;
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('admin.users.search_placeholder')}
          className="input pl-10 w-full max-w-md"
        />
      </div>

      {/* Users Table */}
      {sortedAndFilteredUsers.length === 0 ? (
        <div className="card-elevated p-8 text-center text-text-muted">
          {t('admin.users.no_users')}
        </div>
      ) : (
        <div className="card-elevated overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-alt">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">
                    <button
                      onClick={() => handleSort('email')}
                      className="flex items-center gap-1 hover:text-text transition-colors"
                    >
                      {t('admin.users.email')}
                      {sortBy === 'email' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} className="opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-1 hover:text-text transition-colors"
                    >
                      {t('admin.users.status')}
                      {sortBy === 'status' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} className="opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center gap-1 hover:text-text transition-colors"
                    >
                      {t('admin.users.created')}
                      {sortBy === 'createdAt' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} className="opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-text-muted">
                    <button
                      onClick={() => handleSort('role')}
                      className="flex items-center gap-1 hover:text-text transition-colors"
                    >
                      {t('admin.users.role')}
                      {sortBy === 'role' ? (
                        sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} className="opacity-30" />
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedAndFilteredUsers.map(user => {
                  const isAdmin = user.groups?.includes('admin') || false;
                  return (
                    <tr key={user.userId} className="hover:bg-surface-alt">
                      <td className="px-4 py-3">
                        <span className="font-medium text-text">{user.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge text-xs ${getStatusBadge(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-muted">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        {isAdmin ? (
                          <span className="inline-flex items-center gap-1 text-primary font-medium">
                            <Shield size={14} />
                            {t('admin.users.is_admin')}
                          </span>
                        ) : (
                          <span className="text-text-muted">{t('admin.users.user')}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Load More Button */}
      {nextToken && (
        <div className="flex justify-center">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="btn-secondary flex items-center gap-2"
          >
            {loadingMore ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <ChevronRight size={18} />
            )}
            {t('admin.users.load_more')}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserManagementTab;
